import { CAREER_TIERS } from '@crowdfpl/domain/gamification';
import { Button } from '@crowdfpl/ui-web/button';
import { Card } from '@crowdfpl/ui-web/card';
import { Progress } from '@crowdfpl/ui-web/progress';
import { Skeleton } from '@crowdfpl/ui-web/skeleton';
import { AlertCircle, TrendingUp } from 'lucide-react';
import { type ReactElement } from 'react';
import { CAREER_TIER_ICONS } from '@/components/gamification/career-tier-icons';
import { formatCareerTierLabel } from '@/components/gamification/career-tier-labels';
import { CareerTierTimeline } from '@/components/gamification/career-tier-timeline';
import {
  clampSegmentProgress,
  identityAccentClass,
  identityIconSurfaceClass,
  identityTitle,
  isMaxTierProgress,
  isNewManagerProgress,
  nextTierCopy,
  type ProgressState,
} from '@/components/gamification/progress-identity';
import { XpBreakdownGrid } from '@/components/gamification/xp-breakdown-grid';
import { cn } from '@/helpers/utils';

function LoadingSkeleton(): ReactElement {
  return (
    <div className="space-y-4" data-testid="manager-tiers-loading" aria-busy="true" aria-label="Loading progress">
      <div className="flex items-center gap-3 sm:gap-4">
        <Skeleton className="h-12 w-12 shrink-0 rounded-2xl sm:h-14 sm:w-14" />
        <div className="min-w-0 flex-1 space-y-2">
          <Skeleton className="h-7 w-40" />
          <Skeleton className="h-5 w-28" />
          <Skeleton className="h-4 w-48" />
        </div>
      </div>
      <Skeleton className="h-16 w-full rounded-lg" />
      <Skeleton className="h-20 w-full rounded-xl" />
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Skeleton className="h-24 rounded-lg" />
        <Skeleton className="h-24 rounded-lg" />
        <Skeleton className="h-24 rounded-lg" />
        <Skeleton className="h-24 rounded-lg" />
      </div>
    </div>
  );
}

function ErrorState({ onRetry }: { onRetry?: () => void }): ReactElement {
  return (
    <div
      className="flex flex-col items-start gap-3 rounded-lg border border-border bg-card p-4 sm:p-5"
      data-testid="manager-tiers-error"
      role="alert"
    >
      <div className="flex items-center gap-2 text-foreground">
        <AlertCircle className="h-4 w-4 text-destructive" aria-hidden />
        <p className="text-sm font-medium">Could not load progress data.</p>
      </div>
      <p className="text-xs text-muted-foreground">Please try again.</p>
      {onRetry ? (
        <Button type="button" variant="outline" className="min-h-11" onClick={onRetry}>
          Retry
        </Button>
      ) : null}
    </div>
  );
}

type ManagerTiersViewProps = {
  progress?: ProgressState;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  className?: string;
};

/**
 * Progress tab body: identity, career timeline, segment progress, XP breakdown.
 * Tier/percentile come from the API read model — no client recalculation.
 */
export function ManagerTiersView({
  progress,
  isLoading = false,
  isError = false,
  onRetry,
  className,
}: ManagerTiersViewProps): ReactElement {
  if (isLoading) {
    return (
      <Card className={cn('space-y-4 p-4 sm:space-y-5 sm:p-5', className)} data-testid="manager-tiers-view">
        <LoadingSkeleton />
      </Card>
    );
  }

  if (isError || !progress) {
    return (
      <Card className={cn('p-0', className)} data-testid="manager-tiers-view">
        <ErrorState onRetry={onRetry} />
      </Card>
    );
  }

  const tierLabel = formatCareerTierLabel(progress.tier);
  const title = identityTitle(progress);
  const Icon = CAREER_TIER_ICONS[progress.tier];
  const segmentProgress = clampSegmentProgress(progress.segment_progress);
  const segmentPercent = Math.round(segmentProgress * 100);
  const maxTier = isMaxTierProgress(progress);
  const showPercentile = !isNewManagerProgress(progress) && progress.top_percent != null;
  const progressTargetLabel = maxTier
    ? tierLabel
    : progress.next_tier
      ? formatCareerTierLabel(progress.next_tier)
      : tierLabel;
  const segmentCeilingXp = maxTier
    ? CAREER_TIERS.legend
    : progress.next_tier
      ? CAREER_TIERS[progress.next_tier]
      : CAREER_TIERS[progress.tier];

  return (
    <Card
      className={cn('space-y-4 p-4 sm:space-y-5 sm:p-5', className)}
      data-testid="manager-tiers-view"
      aria-label="Manager progress"
    >
      <div className="flex items-center gap-3 sm:gap-4">
        <div
          className={cn(
            'flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border-2 sm:h-14 sm:w-14',
            identityIconSurfaceClass(progress.tier),
          )}
        >
          <Icon className="h-6 w-6 sm:h-7 sm:w-7" strokeWidth={2.2} aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className={cn('text-2xl font-black leading-none tracking-tight sm:text-3xl', identityAccentClass(progress.tier))}>
            {title}
          </h3>
          <p className="mt-1.5 text-sm font-bold tabular-nums text-foreground sm:text-base">
            {progress.xp.toLocaleString()} XP
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] tabular-nums text-muted-foreground sm:text-xs">{nextTierCopy(progress)}</span>
            {showPercentile ? (
              <span
                data-testid="manager-tiers-percentile"
                className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold text-primary"
              >
                <TrendingUp className="h-3 w-3" aria-hidden />
                Top {progress.top_percent}%
              </span>
            ) : null}
          </div>
        </div>
      </div>

      <CareerTierTimeline currentTier={progress.tier} />

      <div className="rounded-xl border border-border/60 bg-muted/30 p-3.5 sm:p-4">
        <div className="flex items-baseline justify-between gap-2">
          <p className="text-[13px] font-semibold text-foreground sm:text-sm">
            {maxTier ? 'Max tier reached' : `Progress to ${progressTargetLabel}`}
          </p>
          <p
            data-testid="manager-tiers-segment-percent"
            className="text-[13px] font-bold tabular-nums text-primary sm:text-sm"
          >
            {segmentPercent}%
          </p>
        </div>
        <Progress
          value={segmentPercent}
          className="mt-2 h-2 motion-reduce:[&>div]:transition-none"
          aria-label={`Segment progress ${segmentPercent} percent`}
        />
        <div className="mt-1.5 flex justify-between text-[11px] tabular-nums">
          <span className="font-semibold text-foreground/70">{progress.xp.toLocaleString()} XP</span>
          <span className="text-muted-foreground">{segmentCeilingXp.toLocaleString()} XP</span>
        </div>
      </div>

      <XpBreakdownGrid breakdown={progress.breakdown} />
    </Card>
  );
}
